import { useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { pickPricing } from "./pricingConfig";

/**
 * One pricing tier card.
 *
 * Shape of `p`:
 *   { id, name, price, tagline, cta, primary[], features[], missing[],
 *     ribbon?, popular?, note?, pricing? }
 *
 * `intervalId` — "monthly" | "biannual" | "annual" — comes from the parent
 * Landing/BillingTab toggle. Free plan has `pricing: null` and is not
 * affected by the interval; its $0 / 30-day copy is preserved.
 */
export default function PricingCard({ p, onPick, intervalId = "monthly" }) {
  const [open, setOpen] = useState(false);
  const price = pickPricing(p, intervalId);

  // Free plan (no `pricing` block) — keep legacy $0 / 30-day layout unchanged.
  const isFree = !price;

  const isAnnualWithDiscount =
    !isFree && intervalId === "annual" && price?.reference_amount_usd && price?.discount_percent;
  const isBiAnnual = !isFree && intervalId === "biannual";
  const includedDomain = !isFree && !!price?.free_domain_first_site;
  const nonRefundable = !isFree && !!price?.non_refundable;

  return (
    <div
      data-testid={`landing-plan-${p.id}`}
      className={`iti-card p-6 flex flex-col relative ${p.popular ? "iti-most-popular" : ""}`}
    >
      {p.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-extrabold tracking-wider px-3 py-1 rounded-full shadow">
          MOST POPULAR
        </div>
      )}
      {p.ribbon && (
        <div className="absolute -top-2 left-5 bg-emerald-50 text-emerald-700 text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-full border border-emerald-200">
          {p.ribbon}
        </div>
      )}
      {isAnnualWithDiscount && (
        <div
          className="absolute -top-3 right-5 bg-amber-400 text-amber-950 text-[10px] font-extrabold tracking-wider px-2.5 py-1 rounded-full shadow"
          data-testid={`plan-save-badge-${p.id}`}
        >
          Save {price.discount_percent}%
        </div>
      )}

      <div className="mt-2">
        <div className="font-display text-2xl font-extrabold text-slate-900">{p.name}</div>
        <div className="text-xs text-slate-500 mt-1 min-h-[34px]">{p.tagline}</div>
      </div>

      {isFree ? (
        <div className="mt-3 flex items-end gap-1">
          <div className="font-display text-4xl font-extrabold text-slate-900">$0</div>
          <div className="pb-1 text-slate-500 text-sm">/mo</div>
        </div>
      ) : (
        <>
          {isAnnualWithDiscount && (
            <div className="mt-3 text-sm text-slate-400 line-through leading-none"
                 data-testid={`plan-ref-price-${p.id}`}>
              ${price.reference_amount_usd}/year
            </div>
          )}
          <div className="mt-1 flex items-end gap-1"
               data-testid={`plan-amount-${p.id}`}>
            <div className="font-display text-4xl font-extrabold text-slate-900">
              ${price.amount_usd}
            </div>
            <div className="pb-1 text-slate-500 text-sm">
              {intervalId === "monthly" ? "/mo"
                : intervalId === "biannual" ? "/ 6 mo"
                : "/year"}
            </div>
          </div>
          {intervalId === "annual" && (
            <div className="text-[11px] mt-0.5 text-slate-500"
                 data-testid={`plan-eqmo-${p.id}`}>
              ≈ ${price.equivalent_monthly_usd}/mo billed annually
            </div>
          )}
          {intervalId === "biannual" && (
            <div className="text-[11px] mt-0.5 text-slate-500">
              Billed every 6 months
            </div>
          )}
        </>
      )}
      {p.note && isFree && <div className="text-[11px] mt-0.5 text-slate-500">{p.note}</div>}

      {includedDomain && (
        <div className="mt-3 inline-flex items-center gap-1.5 self-start text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
             data-testid={`plan-domain-badge-${p.id}`}>
          <Globe className="w-3 h-3" /> Free standard domain included
        </div>
      )}
      {nonRefundable && (
        <div className="mt-2 text-[10px] text-slate-400 self-start"
             data-testid={`plan-non-refundable-${p.id}`}>
          Annual plans are non-refundable.
        </div>
      )}

      <button
        onClick={() => onPick(p.id, intervalId)}
        data-testid={`pick-plan-${p.id}`}
        className="iti-btn-primary mt-5 py-2.5 text-sm font-semibold"
      >
        {p.cta}
      </button>

      <div className="mt-5 space-y-2 text-sm">
        {p.primary.map((f, i) => (
          <div key={i} className="flex items-start gap-2 text-slate-700">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={3} />
            <span>{f}</span>
          </div>
        ))}
      </div>

      {(p.features.length > 0 || p.missing.length > 0) && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={`plan-accordion-${p.id}`}
            aria-expanded={open}
            className="mt-4 self-start text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 hover:underline"
          >
            {open ? "Hide full features" : "See all features"}
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div
              className="mt-3 space-y-2 text-sm border-t border-emerald-100/80 pt-3"
              data-testid={`plan-features-${p.id}`}
            >
              {p.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-slate-700">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={3} />
                  <span>{f}</span>
                </div>
              ))}
              {(p.missing || []).map((f, i) => (
                <div key={`m-${i}`} className="flex items-start gap-2 text-slate-400">
                  <span className="w-4 h-4 mt-0.5 shrink-0 rounded-full border border-slate-300 flex items-center justify-center text-[9px]">×</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
